import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanFieldMapping } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

export interface KanbanCardProps {
  record: Record<string, unknown>;
  fieldMapping: KanbanFieldMapping;
  cardLayout: 'compact' | 'standard' | 'detailed';
  colorMapping?: Record<string, string>;
  isViewerMode: boolean;
  dragEnabled: boolean;
  index: number;
  onClick?: (record: Record<string, unknown>) => void;
  onEditClick?: (itemId: string, itemData: Record<string, unknown>) => void;
}

function getField(record: Record<string, unknown>, fieldName: string | undefined): string {
  if (!fieldName) return '';
  const val = record[fieldName];
  if (val == null) return '';
  if (val instanceof Date) return val.toLocaleDateString();
  return String(val);
}

export function getCardId(record: Record<string, unknown>, fieldMapping: KanbanFieldMapping, index: number): string {
  if (fieldMapping.id) {
    const val = record[fieldMapping.id];
    if (val != null) return String(val);
  }
  const fallback = record._id ?? record.id;
  if (fallback != null) return String(fallback);
  return `fallback-${index}`;
}

export const KanbanCard: React.FC<KanbanCardProps> = ({
  record,
  fieldMapping,
  cardLayout,
  colorMapping,
  isViewerMode,
  dragEnabled,
  index,
  onClick,
  onEditClick,
}) => {
  const cardId = getCardId(record, fieldMapping, index);
  const canDrag = isViewerMode && dragEnabled;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: cardId,
    disabled: !canDrag,
  });

  const title = getField(record, fieldMapping.title);
  const priority = getField(record, fieldMapping.priority);
  const assignee = getField(record, fieldMapping.assignee);
  const dueDate = getField(record, fieldMapping.dueDate);
  const description = getField(record, fieldMapping.description);
  const tagsRaw = record[fieldMapping.tags ?? ''];
  const tags: string[] = Array.isArray(tagsRaw)
    ? tagsRaw.map(String)
    : typeof tagsRaw === 'string' && tagsRaw
      ? tagsRaw.split(',').map(s => s.trim())
      : [];

  // Color-coded left border
  const colorField = fieldMapping.priority || fieldMapping.column;
  const colorValue = record[colorField] as string | undefined;
  const borderColor = (colorMapping && colorValue ? colorMapping[colorValue] : undefined) || pmTheme.border;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    background: pmTheme.surface,
    borderRadius: 6,
    border: `1px solid ${pmTheme.border}`,
    borderLeft: `4px solid ${borderColor}`,
    boxShadow: pmTheme.shadow,
    cursor: canDrag ? 'grab' : 'default',
  };

  const handleClick = () => {
    if (isViewerMode && onClick) {
      onClick(record);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(cardId, record);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`kanban-card-${cardId}`}
      onClick={handleClick}
      {...attributes}
    >
      {/* Drag handle — visible only in viewer mode with drag enabled */}
      {canDrag && (
        <div
          data-testid={`kanban-card-drag-handle-${cardId}`}
          {...listeners}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 4px',
            cursor: 'grab',
            color: pmTheme.textSubtle,
            flexShrink: 0,
            userSelect: 'none',
          }}
          aria-label="Drag handle"
        >
          ⠿
        </div>
      )}

      {/* Card content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: cardLayout === 'compact' ? 0 : 4,
          flex: 1,
          padding: cardLayout === 'compact' ? '6px 10px' : cardLayout === 'standard' ? '8px 12px' : '10px 14px',
          overflow: 'hidden',
        }}
      >
        {/* Title row with edit button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div
            data-testid={`kanban-card-title-${cardId}`}
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: pmTheme.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: cardLayout === 'compact' ? 'nowrap' : undefined,
              flex: 1,
            }}
          >
            {title || 'Untitled'}
          </div>
          {isViewerMode && onEditClick && (
            <button
              data-testid={`kanban-card-edit-${cardId}`}
              onClick={handleEditClick}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                borderRadius: 3,
                color: pmTheme.textSubtle,
                fontSize: 12,
                lineHeight: 1,
                flexShrink: 0,
              }}
              aria-label="Edit item"
              title="Edit item"
            >
              ✎
            </button>
          )}
        </div>

        {/* Standard + Detailed: priority badge + assignee + due date */}
        {cardLayout !== 'compact' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginTop: 2,
            }}
          >
            {priority && (
              <span
                data-testid={`kanban-card-priority-${cardId}`}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: pmTheme.surfaceMuted,
                  color: pmTheme.textMuted,
                }}
              >
                {priority}
              </span>
            )}
            {assignee && (
              <span
                data-testid={`kanban-card-assignee-${cardId}`}
                style={{ fontSize: 11, color: pmTheme.textMuted }}
              >
                {assignee}
              </span>
            )}
            {dueDate && (
              <span
                data-testid={`kanban-card-duedate-${cardId}`}
                style={{ fontSize: 11, color: pmTheme.textSubtle, marginLeft: 'auto' }}
              >
                {dueDate}
              </span>
            )}
          </div>
        )}

        {/* Detailed only: description + tags */}
        {cardLayout === 'detailed' && (
          <>
            {description && (
              <div
                data-testid={`kanban-card-description-${cardId}`}
                style={{
                  fontSize: 12,
                  color: pmTheme.textMuted,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  marginTop: 2,
                }}
              >
                {description}
              </div>
            )}
            {tags.length > 0 && (
              <div
                data-testid={`kanban-card-tags-${cardId}`}
                style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}
              >
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 10,
                      padding: '1px 5px',
                      borderRadius: 3,
                      background: pmTheme.surfaceMuted,
                      color: pmTheme.textMuted,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

KanbanCard.displayName = 'KanbanCard';
